/**
 * Form File Input Component
 * 自动增强原生 file input，使其符合UI库风格
 * 对用户透明，无需修改现有代码
 */

(function(window, document) {
  'use strict';

  // 工具函数：格式化文件大小
  function formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // 工具函数：获取文件图标
  function getFileIcon(file) {
    const type = file.type;
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    if (type.includes('pdf')) return '📄';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    if (type.includes('powerpoint') || type.includes('presentation')) return '📽️';
    if (type.includes('zip') || type.includes('rar') || type.includes('compressed')) return '🗜️';
    if (type.includes('text')) return '📃';
    return '📁';
  }

  // 增强单个文件输入
  function enhanceFileInput(input) {
    // 避免重复增强
    if (input.dataset.uiEnhanced === 'true') return;
    
    // 检查是否已经在自定义包装器中
    if (input.parentElement && input.parentElement.classList.contains('form-file')) {
      return;
    }
    
    // 立即标记为已增强，这会移除 CSS 伪元素占位符
    input.dataset.uiEnhanced = 'true';
    
    // 移除 JS 创建的占位符（如果存在）
    const existingPlaceholder = input.nextElementSibling;
    if (existingPlaceholder && existingPlaceholder.classList.contains('form-file-placeholder')) {
      existingPlaceholder.remove();
    }

    // 创建包装器
    const wrapper = document.createElement('div');
    wrapper.className = 'form-file';
    
    // 复制原始输入的类名
    const inputClasses = input.className.split(' ').filter(cls => 
      cls.includes('sm') || cls.includes('lg') || cls.includes('size')
    );
    
    if (inputClasses.some(cls => cls.includes('sm'))) {
      wrapper.classList.add('form-file-sm');
    } else if (inputClasses.some(cls => cls.includes('lg'))) {
      wrapper.classList.add('form-file-lg');
    }

    // 创建自定义标签
    const label = document.createElement('label');
    label.className = 'form-file-label';
    label.htmlFor = input.id || `file-input-${Date.now()}`;
    
    if (!input.id) {
      input.id = label.htmlFor;
    }

    const textSpan = document.createElement('span');
    textSpan.className = 'form-file-text';
    textSpan.textContent = input.getAttribute('placeholder') || '选择文件...';

    const buttonSpan = document.createElement('span');
    buttonSpan.className = 'form-file-button';
    buttonSpan.textContent = '浏览';

    label.appendChild(textSpan);
    label.appendChild(buttonSpan);

    // 批量 DOM 操作减少重排
    const parentNode = input.parentNode;
    const nextSibling = input.nextSibling;
    
    // 为原始输入添加自定义类（在移动前）
    input.classList.add('form-file-input');
    
    // 构建完整结构
    wrapper.appendChild(input);
    wrapper.appendChild(label);
    
    // 一次性插入到 DOM（无动画，立即显示）
    if (nextSibling) {
      parentNode.insertBefore(wrapper, nextSibling);
    } else {
      parentNode.appendChild(wrapper);
    }

    // 是否为多选
    const isMultiple = input.hasAttribute('multiple');
    if (isMultiple) {
      wrapper.classList.add('form-file-multiple');
    }

    // 更新文件显示
    function updateFileDisplay() {
      const files = input.files;
      
      if (files && files.length > 0) {
        textSpan.classList.add('has-file');
        
        if (isMultiple) {
          if (files.length === 1) {
            textSpan.innerHTML = `<span>${files[0].name}</span>`;
          } else {
            textSpan.innerHTML = `<span class="file-count">${files.length}</span><span>个文件</span>`;
          }
        } else {
          textSpan.textContent = files[0].name;
        }
        
        // 修改图标为已选择状态
        label.style.setProperty('--file-icon', '"✓"');
      } else {
        textSpan.classList.remove('has-file');
        textSpan.textContent = input.getAttribute('placeholder') || '选择文件...';
        label.style.removeProperty('--file-icon');
      }

      // 触发自定义事件
      input.dispatchEvent(new CustomEvent('ui:file:change', {
        bubbles: true,
        detail: { files: Array.from(files || []) }
      }));
    }

    // 监听文件选择变化
    if (typeof jQuery !== 'undefined') {
      jQuery(input).on('change', updateFileDisplay);
    } else {
      input.addEventListener('change', updateFileDisplay);
    }

    // 聚焦和失焦效果
    input.addEventListener('focus', () => wrapper.classList.add('is-focused'));
    input.addEventListener('blur', () => wrapper.classList.remove('is-focused'));

    // 拖放支持
    if (input.dataset.dropzone !== 'false') {
      let dragCounter = 0;

      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        wrapper.addEventListener(eventName, preventDefaults, false);
      });

      function preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
      }

      wrapper.addEventListener('dragenter', () => {
        dragCounter++;
        wrapper.classList.add('is-dragover');
      });

      wrapper.addEventListener('dragleave', () => {
        dragCounter--;
        if (dragCounter === 0) {
          wrapper.classList.remove('is-dragover');
        }
      });

      wrapper.addEventListener('drop', (e) => {
        dragCounter = 0;
        wrapper.classList.remove('is-dragover');
        
        const dt = e.dataTransfer;
        const files = dt.files;

        if (files.length > 0) {
          input.files = files;
          updateFileDisplay();
        }
      });
    }

    // 存储引用供外部使用
    input._uiFileWrapper = wrapper;
    input._uiFileLabel = label;
    input._uiFileText = textSpan;
  }

  // 自动增强所有 file input
  function autoEnhance() {
    const fileInputs = document.querySelectorAll('input[type="file"]:not([data-ui-enhanced])');
    
    // 立即处理
    if (fileInputs.length > 0) {
      fileInputs.forEach(enhanceFileInput);
    }
  }

  // DOM 加载完成后自动增强
  const init = () => {
    // CSS 伪元素已经提供了占位符，直接增强即可
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        requestAnimationFrame(autoEnhance);
      });
    } else {
      setTimeout(autoEnhance, 0);
    }
  };

  if (document.readyState === 'loading') {
    // DOM 还在加载，等待 DOMContentLoaded
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM 已就绪，立即执行
    init();
  }

  // 监听动态添加的元素
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.matches && node.matches('input[type="file"]')) {
              // CSS 伪元素会自动提供占位符，直接增强
              requestAnimationFrame(() => enhanceFileInput(node));
            }
            // 检查子元素
            if (node.querySelectorAll) {
              const fileInputs = node.querySelectorAll('input[type="file"]:not([data-ui-enhanced])');
              fileInputs.forEach(input => {
                requestAnimationFrame(() => enhanceFileInput(input));
              });
            }
          }
        });
      });
    });

    // 确保 document.body 存在后再开始监听
    const startObserving = () => {
      if (document.body) {
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      }
    };

    if (document.body) {
      startObserving();
    } else {
      document.addEventListener('DOMContentLoaded', startObserving);
    }
  }

  // 公共 API
  window.UIFormFile = {
    // 手动增强特定元素
    enhance: function(element) {
      if (typeof element === 'string') {
        element = document.querySelector(element);
      }
      if (element && element.type === 'file') {
        enhanceFileInput(element);
      }
    },

    // 增强所有未增强的文件输入
    enhanceAll: autoEnhance,

    // 获取文件信息
    getFiles: function(input) {
      if (typeof input === 'string') {
        input = document.querySelector(input);
      }
      if (input && input.files) {
        return Array.from(input.files).map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          formattedSize: formatFileSize(file.size),
          icon: getFileIcon(file),
          file: file
        }));
      }
      return [];
    },

    // 清空文件
    clear: function(input) {
      if (typeof input === 'string') {
        input = document.querySelector(input);
      }
      if (input) {
        input.value = '';
        const event = new Event('change', { bubbles: true });
        input.dispatchEvent(event);
      }
    },

    // 设置验证状态
    setValidation: function(input, state) {
      if (typeof input === 'string') {
        input = document.querySelector(input);
      }
      if (input && input._uiFileWrapper) {
        const wrapper = input._uiFileWrapper;
        wrapper.classList.remove('is-valid', 'is-invalid');
        if (state === 'valid') {
          wrapper.classList.add('is-valid');
        } else if (state === 'invalid') {
          wrapper.classList.add('is-invalid');
        }
      }
    },

    // 工具函数
    formatFileSize: formatFileSize,
    getFileIcon: getFileIcon
  };

  // jQuery 插件（如果 jQuery 可用）
  if (typeof jQuery !== 'undefined') {
    jQuery.fn.uiFormFile = function(action, ...args) {
      if (action === 'files') {
        return window.UIFormFile.getFiles(this[0]);
      } else if (action === 'clear') {
        this.each(function() {
          window.UIFormFile.clear(this);
        });
        return this;
      } else if (action === 'validate') {
        this.each(function() {
          window.UIFormFile.setValidation(this, args[0]);
        });
        return this;
      } else {
        // 默认：增强元素
        this.each(function() {
          if (this.type === 'file') {
            enhanceFileInput(this);
          }
        });
        return this;
      }
    };
  }

})(window, document);
